import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

// Create static HTML structure instead of React components for better compatibility
const createPDFContent = ({ nodes, edges, phaseData, frameworkPhases, sessionName }) => {
  // Filter out phase nodes that have no children
  const filteredNodes = nodes.filter(node => {
    if (node.type === 'phase') {
      const hasChildren = edges.some(edge => edge.source === node.id);
      return hasChildren;
    }
    return true;
  });

  const getNodeSize = (node) => {
    if (node.size === 'small') return 6;
    if (node.type === 'center') return 16;
    if (node.type === 'phase') return 12;
    if (node.type === 'ability') return 10;
    return 8;
  };

  const getNodeStroke = (node) => {
    if (node.type === 'center') return '#fffff';
    if (node.type === 'phase') return '#fffff';
    return '#ffffff';
  };

  const getPhaseColor = (phase) => {
    const colors = {
      empathy: { bg: '#ffffff', border: '#d1d5db' },
      define: { bg: '#ffffff', border: '#d1d5db' },
      ideate: { bg: '#ffffff', border: '#d1d5db' },
      prototype: { bg: '#ffffff', border: '#d1d5db' },
      test: { bg: '#ffffff', border: '#d1d5db' },
      communicate: { bg: '#ffffff', border: '#d1d5db' }
    };
    return colors[phase] || { bg: '#ffffff', border: '#d1d5db' };
  };

  const totalAbilities = Object.values(phaseData).reduce((total, phase) => 
    total + (phase.abilities ? phase.abilities.length : 0), 0
  );

  const activePhases = Object.keys(phaseData).filter(key => 
    phaseData[key].abilities && phaseData[key].abilities.length > 0
  ).length;

  const totalEntries = Object.values(phaseData).reduce((total, phase) => {
    const categories = ['skills', 'knowledge', 'mindset', 'tools'];
    return total + categories.reduce((catTotal, category) => {
      const categoryData = phase[category];
      return catTotal + (categoryData ? categoryData.length : 0);
    }, 0);
  }, 0);

  const completionRate = Math.round((activePhases / frameworkPhases.length) * 100);

  // Build SVG for mindmap
  let svgContent = '';
  
  // Render edges
  edges.forEach(edge => {
    const sourceNode = filteredNodes.find(n => n.id === edge.source);
    const targetNode = filteredNodes.find(n => n.id === edge.target);
    
    if (sourceNode && targetNode) {
      svgContent += `<line x1="${sourceNode.x}" y1="${sourceNode.y}" x2="${targetNode.x}" y2="${targetNode.y}" stroke="#D1D5DB" stroke-width="2" opacity="0.6"/>`;
    }
  });
  
  // Render nodes (without labels)
  filteredNodes.forEach(node => {
    const size = getNodeSize(node);
    const stroke = getNodeStroke(node);
    svgContent += `<circle cx="${node.x}" cy="${node.y}" r="${size}" fill="${node.color || '#e3daeb'}" stroke="${stroke}" stroke-width="2"/>`;
  });

  // Build phase sections HTML
  let phaseSectionsHTML = '';
  let phaseCount = 0;
  
  frameworkPhases.forEach(phaseName => {
    const phaseKey = phaseName.toLowerCase();
    const currentPhaseData = phaseData[phaseKey];
    
    if (!currentPhaseData || !currentPhaseData.abilities || currentPhaseData.abilities.length === 0) {
      return;
    }

    // Add page break after every 2 phases
    if (phaseCount > 0 && phaseCount % 2 === 0) {
      phaseSectionsHTML += '<div style="page-break-before: always;"></div>';
    }
    
    phaseCount++;
    const colors = getPhaseColor(phaseKey);
    
    let abilitiesHTML = '';
    if (currentPhaseData.abilities && currentPhaseData.abilities.length > 0) {
      currentPhaseData.abilities.forEach(ability => {
        abilitiesHTML += `<span style="padding-bottom: 15px; ; padding-left: 10px; padding-right: 10px; background-color: #ffffff; color: #6b7280; border: 1px solid #9ca3af; border-radius: 9999px; font-size: 13px; font-weight: 500; display: inline-block; margin-right: 8px; margin-bottom: 8px; line-height: 1.2; vertical-align: middle;">${ability}</span>`;
      });
    }

    let competenciesHTML = '<div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px;">';
    
    // Skills
    if (currentPhaseData.skills && currentPhaseData.skills.length > 0) {
      let skillsHTML = '';
      currentPhaseData.skills.forEach(item => {
        skillsHTML += `<span style="padding-bottom: 15px; ; padding-left: 10px; padding-right: 10px; background-color: #ffffff; color: #62BB67; border: 1px solid #62BB67; border-radius: 4px; font-size: 11px; display: inline-block; margin-right: 6px; margin-bottom: 6px; line-height: 1.3; vertical-align: middle;">${item}</span>`;
      });
      competenciesHTML += `<div><h5 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Skills</h5><div>${skillsHTML}</div></div>`;
    }
    
    // Knowledge
    if (currentPhaseData.knowledge && currentPhaseData.knowledge.length > 0) {
      let knowledgeHTML = '';
      currentPhaseData.knowledge.forEach(item => {
        knowledgeHTML += `<span style="padding-bottom: 15px; ; padding-left: 10px; padding-right: 10px; background-color: #ffffff; color: #378FC0; border: 1px solid #378FC0; border-radius: 4px; font-size: 11px; display: inline-block; margin-right: 6px; margin-bottom: 6px; line-height: 1.3; vertical-align: middle;">${item}</span>`;
      });
      competenciesHTML += `<div><h5 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Knowledge</h5><div>${knowledgeHTML}</div></div>`;
    }
    
    // Mindset
    if (currentPhaseData.mindset && currentPhaseData.mindset.length > 0) {
      let mindsetHTML = '';
      currentPhaseData.mindset.forEach(item => {
        mindsetHTML += `<span style="padding-bottom: 15px; ; padding-left: 10px; padding-right: 10px; background-color: #ffffff; color: #E4A204; border: 1px solid #E4A204; border-radius: 4px; font-size: 11px; display: inline-block; margin-right: 6px; margin-bottom: 6px; line-height: 1.3; vertical-align: middle;">${item}</span>`;
      });
      competenciesHTML += `<div><h5 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Mindset</h5><div>${mindsetHTML}</div></div>`;
    }
    
    // Tools
    if (currentPhaseData.tools && currentPhaseData.tools.length > 0) {
      let toolsHTML = '';
      currentPhaseData.tools.forEach(item => {
        toolsHTML += `<span style="padding-bottom: 15px; ; padding-left: 10px; padding-right: 10px; background-color: #ffffff; color: #7C1586; border: 1px solid #7C1586; border-radius: 4px; font-size: 11px; display: inline-block; margin-right: 6px; margin-bottom: 6px; line-height: 1.3; vertical-align: middle;">${item}</span>`;
      });
      competenciesHTML += `<div><h5 style="font-size: 12px; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Instruments</h5><div>${toolsHTML}</div></div>`;
    }
    
    competenciesHTML += '</div>';

    phaseSectionsHTML += `
      <div style="margin-bottom: 32px; padding: 24px; background-color: ${colors.bg}; border: 2px solid ${colors.border}; border-radius: 8px; page-break-inside: avoid;" data-phase-section="true">
        <h3 style="font-size: 20px; font-weight: bold; color: #1f2937; margin-bottom: 16px; text-transform: capitalize;">${phaseName} Phase</h3>
        
        ${abilitiesHTML ? `<div style="margin-bottom: 20px;">
          <h4 style="font-size: 14px; font-weight: 600; color: #4b5563; text-transform: uppercase; letter-spacing: 0.05em; margin-bottom: 8px;">Abilities</h4>
          <div>${abilitiesHTML}</div>
        </div>` : ''}
        
        <div>
          <h4 style="font-size: 16px; font-weight: 600; color: #374151; margin-bottom: 16px; padding-bottom: 8px; border-bottom: 1px solid #d1d5db;">Phase Competencies</h4>
          ${competenciesHTML}
        </div>
      </div>
    `;
  });

  // Return complete HTML structure
  return `
    <div style="font-family: system-ui, -apple-system, sans-serif; padding: 40px; background-color: #ffffff; color: #1f2937; width: 794px;">
      <!-- Header -->
      <div style="margin-bottom: 32px; border-bottom: 3px solid #ebdaf7; padding-bottom: 16px;">
        <h1 style="font-size: 32px; font-weight: bold; color: #1f2937; margin: 0 0 8px 0;">Design Competencies Map</h1>
        ${sessionName ? `<p style="font-size: 16px; color: #6b7280; margin: 0;">Project: ${sessionName}</p>` : ''}
        <p style="font-size: 12px; color: #9ca3af; margin: 4px 0 0 0;">Generated on ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
      </div>

      <!-- Mindmap Visualization -->
      ${filteredNodes.length > 0 ? `
        <div style="margin-bottom: 40px;">
          <h2 style="font-size: 24px; font-weight: bold; color: #374151; margin-bottom: 16px;">Visualisation</h2>
          <svg width="100%" height="500" viewBox="0 0 800 600" style="border: 2px solid #e5e7eb; border-radius: 8px; background-color: #ffffff;">
            ${svgContent}
          </svg>
          <div style="margin-top: 16px; display: flex; flex-wrap: wrap; gap: 16px; font-size: 12px;">
            <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 10px; height: 10px; border-radius: 50%; background-color: #62BB67;"></div><span>Skills</span></div>
            <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 10px; height: 10px; border-radius: 50%; background-color: #378FC0;"></div><span>Knowledge</span></div>
            <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 10px; height: 10px; border-radius: 50%; background-color: #E4A204;"></div><span>Mindset</span></div>
            <div style="display: flex; align-items: center; gap: 6px;"><div style="width: 10px; height: 10px; border-radius: 50%; background-color: #7C1586;"></div><span>Instruments</span></div>
          </div>
        </div>
      ` : ''}

      <!-- Summary Statistics -->
      <div style="margin-bottom: 40px;">
        <h2 style="font-size: 24px; font-weight: bold; color: #374151; margin-bottom: 16px;">Summary</h2>
        <div style="display: grid; grid-template-columns: repeat(2, 1fr); gap: 16px;">
          <div style="padding: 20px; background-color: #ffffff; border: 2px solid #d1d5db; border-radius: 8px; text-align: center;">
            <div style="font-size: 36px; font-weight: bold; color: #16a34a;">${totalAbilities}</div>
            <div style="font-size: 14px; color: #4b5563; margin-top: 4px;">Total Abilities</div>
          </div>
          <div style="padding: 20px; background-color: #ffffff; border: 2px solid #d1d5db; border-radius: 8px; text-align: center;">
            <div style="font-size: 36px; font-weight: bold; color: #2563eb;">${activePhases}</div>
            <div style="font-size: 14px; color: #4b5563; margin-top: 4px;">Active Phases</div>
          </div>

        </div>
      </div>

      <!-- Design Competencies by Phase -->
      <div style="page-break-before: always; padding-top: 40px;">
        <h2 style="font-size: 24px; font-weight: bold; color: #374151; margin-bottom: 24px;">Competencies</h2>
        ${phaseSectionsHTML}
      </div>

      <!-- Footer -->
      <div style="margin-top: 40px; padding-top: 20px; border-top: 2px solid #e5e7eb; text-align: center; color: #9ca3af; font-size: 12px;">
        <p style="margin: 0;">Design Competencies Map • Generated by Mindmap Tool</p>
      </div>
    </div>
  `;
};

// Main PDF Generation Function
const generatePDF = async ({ 
  nodes = [], 
  edges = [], 
  phaseData = {}, 
  frameworkPhases = [],
  sessionName = 'Mindmap Export'
}) => {
  try {
    // Create a temporary container
    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.left = '-9999px';
    container.style.top = '0';
    container.innerHTML = createPDFContent({ nodes, edges, phaseData, frameworkPhases, sessionName });
    document.body.appendChild(container);

    // Wait for content to render
    await new Promise(resolve => setTimeout(resolve, 500));

    // Simple approach: render sections separately based on content structure
    const pdf = new jsPDF('p', 'mm', 'a4');
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    
    // Parse the container content to find sections
    const containerDiv = container.querySelector('div[style*="padding: 40px"]');
    
    if (!containerDiv) {
      // Fallback: render entire container
      const canvas = await html2canvas(container, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      // Only add content if it fits on one page or needs multiple pages
      if (imgHeight <= pageHeight) {
        // Fits on one page
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
      } else {
        // Needs multiple pages
        let heightLeft = imgHeight;
        let position = 0;

        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;

        while (heightLeft > 0) {
          position = heightLeft - imgHeight;
          pdf.addPage();
          pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
        }
      }
      
      pdf.save(`${sessionName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);
      document.body.removeChild(container);
      return { success: true };
    }
    
    // Get all child elements
    const allChildren = Array.from(containerDiv.children);
    const sections = [];
    
    // First section: everything before "Design Competencies by Phase"
    let firstSectionElements = [];
    let competenciesStartIndex = -1;
    
    for (let i = 0; i < allChildren.length; i++) {
      const child = allChildren[i];
      const hasPageBreak = child.style.pageBreakBefore === 'always';
      
      if (hasPageBreak) {
        competenciesStartIndex = i;
        break;
      }
      firstSectionElements.push(child);
    }
    
    if (firstSectionElements.length > 0) {
      sections.push(firstSectionElements);
    }
    
    // Handle "Design Competencies by Phase" section
    if (competenciesStartIndex >= 0) {
      const competenciesSection = allChildren[competenciesStartIndex];
      const phaseElements = Array.from(competenciesSection.querySelectorAll('[data-phase-section="true"]'));
      
      // Add header only once on first phase page
      let isFirstPhasePage = true;
      
      // Render each phase individually to avoid cuts
      phaseElements.forEach(phaseElement => {
        const sectionElements = [];
        
        // Clone the competencies section header
        const headerClone = competenciesSection.cloneNode(false);
        const header = competenciesSection.querySelector('h2');
        if (header && isFirstPhasePage) {
          headerClone.appendChild(header.cloneNode(true));
          isFirstPhasePage = false;
        }
        
        // Add this phase
        headerClone.appendChild(phaseElement.cloneNode(true));
        
        sectionElements.push(headerClone);
        sections.push(sectionElements);
      });
    }
    
    // Render each section
    for (let i = 0; i < sections.length; i++) {
      const sectionElements = sections[i];
      
      // Create temporary container for this section
      const tempContainer = document.createElement('div');
      tempContainer.style.position = 'absolute';
      tempContainer.style.left = '-9999px';
      tempContainer.style.width = '794px';
      tempContainer.style.backgroundColor = '#ffffff';
      tempContainer.style.fontFamily = 'system-ui, -apple-system, sans-serif';
      tempContainer.style.padding = '40px';
      tempContainer.style.color = '#1f2937';
      
      sectionElements.forEach(el => {
        tempContainer.appendChild(el.cloneNode(true));
      });
      
      document.body.appendChild(tempContainer);
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const canvas = await html2canvas(tempContainer, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff'
      });
      
      document.body.removeChild(tempContainer);
      
      const imgData = canvas.toDataURL('image/jpeg', 0.95);
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      if (i > 0) {
        pdf.addPage();
      }
      
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;

      while (heightLeft > 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, imgWidth, imgHeight);
        heightLeft -= pageHeight;
      }
    }

    pdf.save(`${sessionName.replace(/\s+/g, '-').toLowerCase()}-${new Date().toISOString().split('T')[0]}.pdf`);

    // Cleanup
    document.body.removeChild(container);

    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
};

export default generatePDF;